import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { useEffect } from 'react';
import { ConnectedViewpointModeMainView } from 'appLayout/ViewpointModeMainViewPane';
import { ConnectedWorkspaceModeMainView } from 'appLayout/WorkspaceModeMainViewPane';
import { connect } from '@ardoq/rxbeach';
import { MAIN_PANE_LEFT } from 'streams/views/mainContent/types';
import { LoadSlideErrorTypes } from './types';
import { viewPane$, ViewPaneState } from './viewPane$';
import { DashboardSlide } from './DashboardSlide';
import { dashboardSlide$ } from './dashboardSlide$';
import { MetamodelSlide as MetamodelSlideBase } from './MetamodelSlide';
import * as profiling from '@ardoq/profiling';
import { ViewPaneElement } from 'atomicComponents/ViewPaneElement';
import { ViewContainer } from 'tabview/ViewContainer';
import { subscribeToAction } from '../../streams/utils/streamUtils';
import { notifyViewRenderDone } from '../../tabview/actions';
import { ReportSlide as ReportSlideBase } from './ReportSlide';
import {
  APISlideAttributes,
  ArdoqId,
  isDashboardSlide,
  isLucidSlide,
  isMetamodelSlide,
  isReportSlide,
  isVisualizationSlide,
} from '@ardoq/api-types';
import { Header1 } from '@ardoq/typography';
import { ErrorSlide } from './atoms';
import { LucidSlide as LucidSlideBase } from './LucidSlide';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { isEqual } from 'lodash';
import { requestedTabs$ } from 'appLayout/requestedTabs$';
import type { HasViewpointMode } from '@ardoq/graph';
import { t, Trans, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

const MainViewForMode = ({ isViewpointMode }: HasViewpointMode) =>
  isViewpointMode ? (
    <ConnectedViewpointModeMainView />
  ) : (
    <ConnectedWorkspaceModeMainView location={MAIN_PANE_LEFT} />
  );

const MainViewContainer = connect(
  MainViewForMode,
  requestedTabs$.pipe(
    map(({ isViewpointMode }) => ({ isViewpointMode })),
    distinctUntilChanged(isEqual)
  )
);
const ConnectedDashboardSlide = connect(DashboardSlide, dashboardSlide$);

const SlideContainer = ({
  slide,
  presentationId,
}: {
  slide: APISlideAttributes | null;
  presentationId?: ArdoqId;
}) => {
  useEffect(() => {
    if (!slide || !isVisualizationSlide(slide)) return;
    const transaction = profiling.startTransaction(
      'slide visualization render',
      1000,
      profiling.Team.ENGAGEMENT
    );
    const subscription = subscribeToAction(notifyViewRenderDone, viewId => {
      profiling.endTransaction(transaction, { viewId });
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [slide]);

  if (slide && isDashboardSlide(slide)) {
    return <ConnectedDashboardSlide />;
  } else if (slide && isReportSlide(slide) && presentationId) {
    return <ReportSlideBase />;
  } else if (slide && isMetamodelSlide(slide)) {
    return <MetamodelSlideBase />;
  } else if (slide && isLucidSlide(slide)) {
    return <LucidSlideBase documentId={slide.lucidDocumentId} />;
  }
  return (
    <ViewContainer>
      <MainViewContainer />
    </ViewContainer>
  );
};

const ViewPane = (
  props: ViewPaneState & {
    presentationId?: ArdoqId;
    i18nLocale: I18nLocale;
  }
) => (
  <ViewPaneElement>
    {props.slideError ? (
      <ErrorSlide>
        {props.slideError === LoadSlideErrorTypes.UNAUTHORIZED ||
        props.slideError ===
          LoadSlideErrorTypes.UNAUTHORIZED_DASHBOARD_SLIDE ? (
          <div>
            <Header1>
              {t(
                props.i18nLocale,
                'presentationAdmin:viewer.errors.noAccess.title',
                "You don't have access to this slide"
              )}
            </Header1>
            <p>
              <Trans
                i18nKey="presentationAdmin:viewer.errors.noAccess.learnMore"
                defaults="Learn more in <knowledgeBaseLink>our knowledge base</knowledgeBaseLink>"
                components={{
                  knowledgeBaseLink: (
                    <a
                      href={KnowledgeBaseLink.PRESENTATIONS_PERMISSIONS}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </p>
            {props.slideError ===
              LoadSlideErrorTypes.UNAUTHORIZED_DASHBOARD_SLIDE && (
              <p>
                <strong>
                  {t(
                    props.i18nLocale,
                    'presentationAdmin:viewer.errors.noAccess.dashboardNote',
                    'N.B:'
                  )}
                </strong>{' '}
                {t(
                  props.i18nLocale,
                  'presentationAdmin:viewer.errors.noAccess.dashboardNoteText',
                  'Dashboard slides are currently only accessible to users who are logged in and have access to the dashboard.'
                )}
              </p>
            )}
          </div>
        ) : (
          <div>
            <Header1>
              {t(
                props.i18nLocale,
                'presentationAdmin:viewer.errors.loadFailed.title',
                'The slide failed to load'
              )}
            </Header1>
            <p>
              {t(
                props.i18nLocale,
                'presentationAdmin:viewer.errors.loadFailed.supportText',
                'We have been notified, but please contact support if the error persists.'
              )}
            </p>
          </div>
        )}
      </ErrorSlide>
    ) : (
      <SlideContainer
        slide={props.loadedSlide}
        presentationId={props.presentationId}
      />
    )}
  </ViewPaneElement>
);

const viewPaneViewModel$ = combineLatest([viewPane$, i18nLocale$]).pipe(
  map(([state, i18nLocale]) => ({
    ...state,
    i18nLocale,
  }))
);

export const viewPane = connect(ViewPane, viewPaneViewModel$);
